/** Geometrie des schwebenden „Bild-in-Bild"-Panels (DraggableSidepanel). */

export const MIN_PANEL_WIDTH = 360;
export const MIN_PANEL_HEIGHT = 320;

/** Anteil des Bildschirms, den das Panel höchstens einnimmt. */
const MAX_WIDTH_RATIO = 0.98;
const MAX_HEIGHT_RATIO = 0.95;

/** Rest, der beim Verschieben sichtbar bleiben muss, damit das Panel
 *  greifbar bleibt. */
const KEEP_VISIBLE_BOTTOM = 80;
const KEEP_VISIBLE_RIGHT = 200;

export interface IPanelBox {
  top: number;
  left: number;
  width: number;
  height: number;
}

export interface IViewport {
  width: number;
  height: number;
}

/** Griff, an dem gezogen wird: unten rechts oder oben rechts. */
export type PanelResizeCorner = 'se' | 'ne';

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

/** Neue Größe nach dem Ziehen an einem Griff.
 *
 *  Am Griff **oben rechts** bleibt die Unterkante stehen: die Höhe wächst nach
 *  oben, `top` wandert entsprechend mit. Sonst würde das Panel beim Ziehen nach
 *  oben nach unten weglaufen. Bis QS #8 gab es nur den Griff unten rechts —
 *  „das versteht man nicht, oben rechts sucht man intuitiv".
 */
export const resizePanel = (
  start: IPanelBox,
  dx: number,
  dy: number,
  corner: PanelResizeCorner,
  viewport: IViewport
): IPanelBox => {
  const maxWidth = viewport.width * MAX_WIDTH_RATIO;
  const maxHeight = viewport.height * MAX_HEIGHT_RATIO;

  const width = clamp(start.width + dx, MIN_PANEL_WIDTH, maxWidth);

  if (corner === 'se') {
    return {
      top: start.top,
      left: start.left,
      width,
      height: clamp(start.height + dy, MIN_PANEL_HEIGHT, maxHeight),
    };
  }

  // Unterkante ist der Festpunkt. Zusaetzlich darf `top` nicht negativ werden,
  // also ist die Hoehe auch durch den Abstand der Unterkante zum oberen
  // Bildschirmrand begrenzt.
  const bottomEdge = start.top + start.height;
  const height = clamp(
    start.height - dy,
    MIN_PANEL_HEIGHT,
    Math.min(maxHeight, bottomEdge)
  );

  return {
    top: bottomEdge - height,
    left: start.left,
    width,
    height,
  };
};

/** Neue Position nach dem Verschieben an der Griffleiste. */
export const dragPanel = (
  start: IPanelBox,
  dx: number,
  dy: number,
  viewport: IViewport
): { top: number; left: number } => ({
  top: clamp(start.top + dy, 0, viewport.height - KEEP_VISIBLE_BOTTOM),
  left: clamp(start.left + dx, 0, viewport.width - KEEP_VISIBLE_RIGHT),
});
